import React, { useRef, useState } from 'react';
import ImageCropModal from '../../common/ImageCropModal';
import { createPortal } from 'react-dom';

interface CheckoutImageProps {
  selected: boolean;
  config: {
    imageUrl?: string;
    align?: 'left' | 'center' | 'right';
    background?: string;
    borderColor?: string;
    borderWidth?: number;
    radius?: number;
    href?: string;
    alt?: string;
  };
  zone?: 'top' | 'side' | 'bottom';
  onSelect: () => void;
  onConfigChange?: (cfg: Partial<CheckoutImageProps['config']>) => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
  dragHandleProps?: any;
  viewMode?: boolean; // Modo visualização (checkout, sem interações)
}

const MAX_SIZE_MB = 5;
const ACCEPTED_TYPES = ['image/png', 'image/jpeg'];

const CheckoutImage: React.FC<CheckoutImageProps> = ({ selected, config, zone = 'top', onSelect, onConfigChange, onDelete, onDuplicate, dragHandleProps, viewMode = false }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [tempImage, setTempImage] = useState<string | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const handleFile = (file: File) => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError('Apenas PNG ou JPG');
      return;
    }
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      setError(`Tamanho máximo de ${MAX_SIZE_MB}MB`);
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      setTempImage(e.target?.result as string);
      setCropModalOpen(true);
      setError(null);
    };
    reader.readAsDataURL(file);
  };

  const handleCropComplete = (croppedImage: string) => {
    if (onConfigChange) onConfigChange({ imageUrl: croppedImage, ...({ src: croppedImage } as any) });
    setCropModalOpen(false);
    setTempImage(null);
  };

  const handleCropCancel = () => {
    setCropModalOpen(false);
    setTempImage(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(true);
  };
  const handleDragLeave = () => setDragActive(false);

  const handleRemove = () => {
    if (onConfigChange) onConfigChange({ imageUrl: undefined, ...({ src: undefined } as any) });
  };

  const handleAlign = (align: 'left' | 'center' | 'right') => {
    if (onConfigChange) onConfigChange({ align });
  };

  const hasImage = Boolean(config.imageUrl || (config as any).src);

  // Modo visualização (checkout): apenas renderizar a imagem, sem cliques, hover ou controles
  if (viewMode) {
    return (
      <div
        className="w-full"
        style={{
          background: config.background || 'transparent',
          borderRadius: config.radius ? `${config.radius}px` : '8px',
        }}
      >
        {hasImage && (
          <img
            src={config.imageUrl || (config as any).src}
            alt={config.alt || 'Imagem'}
            loading="lazy"
            draggable={false}
            className="block w-full h-auto rounded"
            style={{
              borderRadius: config.radius ? `${config.radius}px` : '8px',
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div
      className={`relative group w-full cursor-pointer ${selected ? '' : 'hover:ring-2 hover:ring-blue-300'}`}
      style={{
        background: config.background || '#f4f8fe',
        border: `solid ${config.borderWidth || 1}px ${config.borderColor || '#b3d1fa'}`,
        borderRadius: config.radius ? `${config.radius}px` : '8px',
        minHeight: hasImage ? undefined : (zone === 'side' ? 140 : 267),
        outline: selected ? '2px solid #3b82f6' : 'none',
      }}
    >
      {/* Ícone de seleção no canto superior esquerdo - visível apenas no hover */}
      <div
        className="absolute left-2 top-2 z-20 select-none opacity-0 group-hover:opacity-100 transition-opacity"
        style={{ pointerEvents: 'auto' }}
      >
        <button
          type="button"
          title="Selecionar componente de imagem"
          aria-label="Selecionar componente de imagem"
          className="p-1.5 rounded-full bg-white border shadow text-indigo-600 hover:bg-indigo-50"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onSelect();
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2 12h20M12 2v20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <rect x="4" y="4" width="16" height="16" rx="2" stroke="currentColor" strokeWidth="2" />
          </svg>
        </button>
      </div>

      {/* Ações no topo direito */}
      {selected && (
        <div className="absolute right-2 top-2 z-20 select-none" style={{ pointerEvents: 'auto' }}>
          <div className="flex items-center bg-indigo-600 text-white rounded-md shadow overflow-hidden">
            {/* Excluir */}
            <button
              type="button"
              title="Excluir"
              className="px-3 py-1.5 hover:bg-indigo-500 text-red-200"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setConfirmDeleteOpen(true);
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M9 3h6m-9 4h12m-10 0v12m8-12v12M9 3l1 1h4l1-1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
            </button>
            {/* Duplicar */}
            <button
              type="button"
              title="Duplicar"
              className="px-3 py-1.5 hover:bg-indigo-500"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onDuplicate && onDuplicate();
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="7" y="7" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="2" />
                <rect x="3" y="3" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="2" />
              </svg>
            </button>
            {/* Arrastar */}
            <div
              {...(dragHandleProps || {})}
              title="Arrastar"
              className="px-3 py-1.5 cursor-grab hover:bg-indigo-500 bg-indigo-700/60"
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 3H3v5M21 8V3h-5M3 16v5h5M16 21h5v-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M7 7l-4-4M17 7l4-4M7 17l-4 4M17 17l4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </div>
          </div>
        </div>
      )}
      {/* Dropzone ou preview */}
      <div
        className={`${hasImage ? 'block' : 'flex flex-col items-center justify-center'} w-full ${dragActive ? 'bg-blue-100 border-2 border-blue-400' : ''}`}
        style={{
          cursor: hasImage ? 'default' : 'pointer',
          textAlign: config.align || 'center',
          minHeight: hasImage ? undefined : (zone === 'side' ? '140px' : '267px'),
          padding: hasImage ? '0' : '0',
        }}
        onClick={e => {
          e.stopPropagation();
          // Abrir seleção de imagem apenas quando não há imagem e o componente está selecionado
          if (!hasImage && selected) inputRef.current?.click();
          // Quando já tem imagem, apenas seleciona o componente
          else if (hasImage && !selected) onSelect();
        }}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        {(config.imageUrl || (config as any).src) ? (
          config.href ? (
            <a href={config.href} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="block w-full">
              <img
                src={config.imageUrl || (config as any).src}
                alt={config.alt || 'Imagem'}
                loading="lazy"
                draggable={false}
                className="rounded"
                style={{
                  display: 'block',
                  width: 'auto',
                  height: 'auto',
                  maxWidth: '100%',
                  borderRadius: config.radius ? `${config.radius}px` : '8px',
                  margin: 0
                }}
              />
            </a>
          ) : (
            <img
              src={config.imageUrl || (config as any).src}
              alt={config.alt || 'Imagem'}
              loading="lazy"
              draggable={false}
              className="rounded"
              style={{
                display: 'block',
                width: 'auto',
                height: 'auto',
                maxWidth: '100%',
                borderRadius: config.radius ? `${config.radius}px` : '8px',
                margin: 0
              }}
            />
          )
        ) : (
          <>
            <div className="flex flex-col items-center pointer-events-none select-none">
              <svg width="56" height="56" fill="none" viewBox="0 0 48 48"><rect width="48" height="48" rx="8" fill="#e5eefa" /><path d="M16 32l6-8 5 6 7-10 7 12" stroke="#b3bfcf" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /><rect x="12" y="12" width="24" height="18" rx="2" fill="#f4f8fe" stroke="#b3bfcf" strokeWidth="2" /><circle cx="18" cy="17" r="2" fill="#b3bfcf" /></svg>
              <span className="font-medium text-gray-500 mt-2">Selecione do computador</span>
              <span className="text-xs text-gray-400">ou arraste aqui<br />PNG, JPG até 5 MB</span>
            </div>
            <input ref={inputRef} type="file" accept="image/png,image/jpeg" className="hidden" onChange={handleInputChange} />
          </>
        )}
        {error && <span className="text-xs text-red-500 mt-2">{error}</span>}
      </div>
      {/* Alinhamento (painel flutuante ou lateral) */}
      {selected && (
        <div className="absolute left-4 bottom-2 bg-white bg-opacity-80 rounded shadow px-2 py-1 flex gap-1">
          <button onClick={e => { e.stopPropagation(); handleAlign('left'); }} className={`p-1 rounded ${config.align === 'left' ? 'bg-blue-100 border border-blue-400' : ''}`}> <svg width="18" height="18" fill="none" viewBox="0 0 24 24"><rect x="4" y="6" width="16" height="2" rx="1" fill="#555" /><rect x="4" y="11" width="10" height="2" rx="1" fill="#bbb" /><rect x="4" y="16" width="7" height="2" rx="1" fill="#bbb" /></svg></button>
          <button onClick={e => { e.stopPropagation(); handleAlign('center'); }} className={`p-1 rounded ${!config.align || config.align === 'center' ? 'bg-blue-100 border border-blue-400' : ''}`}> <svg width="18" height="18" fill="none" viewBox="0 0 24 24"><rect x="4" y="6" width="16" height="2" rx="1" fill="#555" /><rect x="7" y="11" width="10" height="2" rx="1" fill="#bbb" /><rect x="9" y="16" width="6" height="2" rx="1" fill="#bbb" /></svg></button>
          <button onClick={e => { e.stopPropagation(); handleAlign('right'); }} className={`p-1 rounded ${config.align === 'right' ? 'bg-blue-100 border border-blue-400' : ''}`}> <svg width="18" height="18" fill="none" viewBox="0 0 24 24"><rect x="4" y="6" width="16" height="2" rx="1" fill="#555" /><rect x="10" y="11" width="10" height="2" rx="1" fill="#bbb" /><rect x="13" y="16" width="7" height="2" rx="1" fill="#bbb" /></svg></button>
          {config.imageUrl && <button onClick={e => { e.stopPropagation(); handleRemove(); }} className="p-1 rounded text-red-500" title="Remover imagem"><svg width="16" height="16" fill="none" viewBox="0 0 24 24"><path d="M6 7h12M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2m2 0v12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V7h12z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg></button>}
        </div>
      )}
      {/* Borda de seleção removida para implementar apenas no hover */}
      {/* Modal de crop (portal) */}
      {cropModalOpen && tempImage && createPortal(
        <ImageCropModal
          imageSrc={tempImage}
          open={cropModalOpen}
          onClose={handleCropCancel}
          onCropComplete={handleCropComplete}
        />,
        window.document.body
      )}

      {/* Modal de confirmação de exclusão (portal) */}
      {confirmDeleteOpen && createPortal(
        <div
          className="fixed inset-0 z-30 flex items-center justify-center"
          onMouseDown={(e) => { e.stopPropagation(); }}
        >
          <div className="absolute inset-0 bg-black/60" />
          <div className="relative z-40 w-[90%] sm:w-[420px] mx-auto">
            <div className="flex flex-col items-stretch gap-3">
              <button
                type="button"
                className="w-full py-3 rounded-md bg-red-600 text-white font-medium shadow hover:bg-red-500"
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmDeleteOpen(false);
                  if (onDelete) onDelete();
                }}
              >
                Excluir componente
              </button>
              <button
                type="button"
                className="w-full py-3 rounded-md bg-white text-gray-900 font-medium shadow border border-gray-200 hover:bg-gray-50"
                onClick={(e) => { e.stopPropagation(); setConfirmDeleteOpen(false); }}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>,
        window.document.body
      )}
      {/* Cleanup blob URL on unmount */}
      {null}
    </div>
  );
};

export default CheckoutImage;
